import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

type Lang = 'GB' | 'US';

export type InsightRow = { label: React.ReactNode; value?: React.ReactNode };

export interface SplitChartWithInsightsProps {
    language: Lang;
    brand: string;
    heading: string; // e.g. "Performance By Media Owner"
    metricWord?: string; // default: GB=Impacts, US=Impressions
    chart: React.ReactNode; // LineChart/BarChart/etc.
    insights: InsightRow[]; // right column rows
    chartHeight?: number; // default 380
    leftPillText?: React.ReactNode;
    rightPillText?: React.ReactNode;
}

export default function SplitChartWithInsights({
    language,
    brand,
    heading,
    metricWord,
    chart,
    insights,
    chartHeight = 380,
    leftPillText,
    rightPillText,
}: SplitChartWithInsightsProps) {
    const metric = metricWord ?? (language === 'GB' ? 'Impacts' : 'Impressions');
    const t = { totalPrefix: 'Total', insightsSuffix: 'Insights' };

    const pillSx = {
        gridRow: '2',
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: brand,
        color: '#fff',
        fontWeight: 900,
        borderRadius: 1.5,
        px: 3,
        py: 1.25,
        mb: 2,
        width: 'fit-content',
        maxWidth: '100%',
        whiteSpace: 'nowrap' as const,
        alignSelf: 'start' as const,
    };

    return (
        <Box
            className="split-grid"
            sx={{
                mt: -1,
                display: 'grid',
                // Use the page-level --split-w on screen; force exact 50/50 in print.
                gridTemplateColumns: 'var(--split-w) calc(100% - var(--split-w))',
                gridTemplateRows: 'auto auto 1fr',
                columnGap: { xs: 3, md: 4 },
                alignItems: 'start',
                '@media print': {
                    gridTemplateColumns: 'var(--split-w) calc(100% - var(--split-w))',
                },
            }}
        >
            {/* Row 1: left heading */}
            <Box sx={{ gridColumn: '1', gridRow: '1' }} pb={8}>
                <Typography variant="h3" sx={{ fontWeight: 900, mb: 0, color: '#111' }}>
                    {heading}
                </Typography>
            </Box>

            {/* Row 2: left pill */}
            <Box sx={{ gridColumn: '1', ...pillSx }}>
                <Typography fontWeight={900}>
                    {leftPillText ?? `${t.totalPrefix} ${metric}`}
                </Typography>
            </Box>

            {/* Row 3: left chart (HARD BOUNDARIES) */}
            <Box
                pt={4}
                className="chartArea"
                sx={{
                    gridColumn: '1',
                    gridRow: '3',
                    height: chartHeight,
                    pr: 2,
                    minWidth: 0, // allow grid shrink
                    overflow: 'hidden', // clip anything extra
                    // Keep Recharts fully inside the left half
                    '& .recharts-responsive-container, & .recharts-wrapper': {
                        width: '100% !important',
                        maxWidth: '100% !important',
                        overflow: 'hidden',
                    },
                    '@media print': {
                        pr: 0,
                    },
                }}
            >
                {chart}
            </Box>

            {/* Row 2: right pill */}
            <Box sx={{ gridColumn: '2', ...pillSx, mb: 3 }}>
                <Typography fontWeight={900}>
                    {rightPillText ?? `${metric} ${t.insightsSuffix}`}
                </Typography>
            </Box>

            {/* Row 3: right insights */}
            <Box sx={{ gridColumn: '2', gridRow: '3' }} pt={8}>
                <Stack spacing={2.5} sx={{ maxWidth: 'min(620px, 95%)' }}>
                    {insights.map((row, i) => (
                        <Stack
                            // eslint-disable-next-line react/no-array-index-key -- labels are ReactNodes (no stable id) and the rows are a static, render-only list
                            key={i}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="baseline"
                            sx={{ gap: 2 }}
                        >
                            <Typography>{row.label}</Typography>
                            <Typography sx={{ fontWeight: 900, color: brand }}>
                                {row.value}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
}
